import React, { useState } from 'react'
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Select,
    SimpleGrid,
    Text,
    useToast,
} from '@chakra-ui/react'

// Paste the web app URL of the NENASA.gs script (new sheet) here
const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbylssqLD96xM2PqH0_40-CHCvCJHmhjL2iK5fmKoDPcmN39N8z1-Dzj0kqQR_9ot4hBCw/exec'

const GRADES = [3, 4, 5, 6, 7, 8, 9, 10, 11]

// Grades 3 to 5 have classes 1-6, grades 6 to 11 have classes 1-11
const classCount = (grade) => (grade >= 3 && grade <= 5 ? 6 : 11)

const emptyMembers = () => [0, 1, 2, 3].map(() => ({ name: '', phone: '' }))

const Registration = () => {
    const toast = useToast()
    const [grade, setGrade] = useState('')
    const [cls, setCls] = useState('')
    const [members, setMembers] = useState(emptyMembers())
    const [busy, setBusy] = useState(false)

    const classes = grade
        ? Array.from({ length: classCount(Number(grade)) }, (_, i) => i + 1)
        : []

    const onGrade = (e) => {
        const g = e.target.value
        setGrade(g)
        // drop the chosen class if it does not exist in the new grade
        if (!g || Number(cls) > classCount(Number(g))) setCls('')
    }

    const setMember = (i, key, value) => {
        setMembers((list) => list.map((m, idx) => (idx === i ? { ...m, [key]: value } : m)))
    }

    const validate = () => {
        if (!grade) return 'Please select your grade.'
        if (!cls) return 'Please select your class.'
        for (let i = 0; i < members.length; i++) {
            if (!members[i].name.trim()) return 'Enter the name of member ' + (i + 1) + '.'
            if (members[i].phone.replace(/\D/g, '').length < 9) {
                return 'Enter a valid phone number for member ' + (i + 1) + '.'
            }
        }
        return ''
    }

    const submit = (e) => {
        e.preventDefault()
        const error = validate()
        if (error) {
            toast({ title: 'Please check the form', description: error, status: 'warning', duration: 4000, isClosable: true })
            return
        }

        if (SCRIPT_URL.indexOf('http') !== 0) {
            toast({ title: 'Not connected', description: 'The registration sheet is not connected yet.', status: 'error', duration: 6000, isClosable: true })
            return
        }

        setBusy(true)
        fetch(SCRIPT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
                action: 'nenasa',
                p: {
                    grade: grade,
                    class: cls,
                    members: members.map((m) => ({ name: m.name.trim(), phone: m.phone.trim() })),
                },
            }),
        })
            .then((r) => r.json())
            .then((r) => {
                if (!r.ok) throw new Error('Not saved')
                toast({
                    title: 'Registration successful',
                    description: 'Your class team has been registered.',
                    status: 'success',
                    duration: 6000,
                    isClosable: true,
                })
                setGrade('')
                setCls('')
                setMembers(emptyMembers())
            })
            .catch(() => {
                toast({
                    title: 'Could not register',
                    description: 'Please check your internet connection and try again.',
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                })
            })
            .finally(() => setBusy(false))
    }

    return (
        <div>
            <Container maxW="100%" bg="gray.100" p={['50px 20px', '80px 30px']} id="register">
                <Box
                    as="form"
                    onSubmit={submit}
                    bg="white"
                    maxW="680px"
                    m="0 auto"
                    p={['30px 22px', '50px 45px']}
                    borderRadius="30px"
                    boxShadow="0 10px 40px rgba(0,0,0,0.12)"
                >
                    <Heading textAlign="center" fontSize={['3xl', '4xl']}>Registration</Heading>
                    <Text textAlign="center" fontSize="sm" mt="10px" mb="35px" textTransform="uppercase">
                        All questions are required.
                    </Text>

                    <Text fontSize="xl" fontWeight="bold" mb="15px" textDecoration="underline">Class Details</Text>
                    <SimpleGrid columns={[1, 2]} spacing="20px" mb="35px">
                        <FormControl isRequired>
                            <FormLabel>Grade</FormLabel>
                            <Select placeholder="Select grade" value={grade} onChange={onGrade}>
                                {GRADES.map((g) => (
                                    <option key={g} value={g}>Grade {g}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Class</FormLabel>
                            <Select
                                placeholder={grade ? 'Select class' : 'Select grade first'}
                                value={cls}
                                onChange={(e) => setCls(e.target.value)}
                                isDisabled={!grade}
                            >
                                {classes.map((c) => (
                                    <option key={c} value={c}>Class {c}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </SimpleGrid>

                    {members.map((m, i) => (
                        <Box key={i} mb="30px">
                            <Text fontSize="xl" fontWeight="bold" mb="15px" textDecoration="underline">Member {i + 1}</Text>
                            <SimpleGrid columns={[1, 2]} spacing="20px">
                                <FormControl isRequired>
                                    <FormLabel>Full Name</FormLabel>
                                    <Input
                                        placeholder="Your Answer"
                                        value={m.name}
                                        onChange={(e) => setMember(i, 'name', e.target.value)}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Phone No. (Whatsapp)</FormLabel>
                                    <Input
                                        type="tel"
                                        inputMode="tel"
                                        placeholder="Your Answer"
                                        value={m.phone}
                                        onChange={(e) => setMember(i, 'phone', e.target.value)}
                                    />
                                </FormControl>
                            </SimpleGrid>
                        </Box>
                    ))}

                    <Button
                        type="submit"
                        isLoading={busy}
                        loadingText="Submitting"
                        w="100%"
                        h="56px"
                        mt="10px"
                        bg="gray.900"
                        color="white"
                        borderRadius="12px"
                        fontSize="lg"
                        _hover={{ bg: 'gray.700' }}
                    >
                        Submit
                    </Button>
                </Box>
            </Container>
        </div>
    )
}

export default Registration
